import { useMemo } from "react";
import { Building2 } from "lucide-react";
import MetricCard from "../ui/MetricCard";
import {
  aggregateAgencyMonth,
  calculateMRR,
  calculateCashCollected,
  calculateOutstandingAR,
  computeDepartmentMRR,
  computeDepartmentCashCollected,
  computeDepartmentNewSales,
  computeDepartmentCashInBank,
  computeDepartmentBudgetHeld,
  computeDepartmentDividends,
  computeDepartmentStripeFees,
  computeAgencyCashInBank,
  computeNextInvoicesDue,
} from "../../lib/calculations";
import { formatUSD } from "../../lib/formatters";
import { today, monthKey, toISO } from "../../lib/dateUtils";

const MONTH_START = (y, m) => `${y}-${String(m).padStart(2, "0")}-01`;
const MONTH_END = (y, m) => `${y}-${String(m).padStart(2, "0")}-31`;

// Agency-wide KPI strip + per-department mini cards.
// Respects the global dateFilter (year + fromMonth + toMonth) so swapping
// the filter in the page header reshapes every number on the page.
export default function AgencySummary({ state, dateFilter, onJumpToDept }) {
  const t = today();
  const calendarYear = t.getFullYear();
  const calendarMonthIdx = t.getMonth();
  // Resolve the active window from the global filter, defaulting to
  // current-year-YTD when the filter is empty.
  const filterYear = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : calendarYear;
  const fromMonth = dateFilter?.fromMonth || 1;
  const toMonthRaw = dateFilter?.toMonth || 12;
  // Cap the upper bound at the current month when looking at the live year —
  // future months have zero collected cash and would distort averages.
  const toMonth = filterYear === calendarYear ? Math.min(toMonthRaw, calendarMonthIdx + 1) : toMonthRaw;
  const currentYear = filterYear; // alias used throughout below
  const currentMonthIdx = toMonth - 1;
  const currentMKey = `${currentYear}-${String(toMonth).padStart(2, "0")}`;
  const fromDate = MONTH_START(currentYear, fromMonth);
  const toDate = MONTH_END(currentYear, toMonth);
  const asOf = toISO(t);

  const departments = state.settings?.departments || [];
  const clients = state.clients || [];
  const offers = state.offers || [];
  const payments = state.payments || [];
  const dividends = state.dividends || [];

  // Agency-wide totals
  const agencyMRR = useMemo(() => calculateMRR(clients, offers), [clients, offers]);

  // Agency-wide Stripe fees YTD — sum per dept + unassigned.
  const stripeFeesYtd = useMemo(() => {
    let total = 0;
    for (const dept of departments) {
      total += computeDepartmentStripeFees(dept, state, fromDate, toDate);
    }
    total += computeDepartmentStripeFees(null, state, fromDate, toDate);
    return total;
  }, [departments, state, fromDate, toDate]);

  const ytdAgency = useMemo(() => {
    let revenue = 0, cogs = 0, opex = 0;
    for (let i = fromMonth - 1; i <= currentMonthIdx; i++) {
      const mKey = `${currentYear}-${String(i + 1).padStart(2, "0")}`;
      const m = aggregateAgencyMonth(state, mKey);
      revenue += m.revenue;
      cogs += m.cogs.total;
      opex += m.opex.total;
    }
    // Net Revenue (after Stripe fees) flows through gross profit → net profit.
    const netRevenue = revenue - stripeFeesYtd;
    const grossAfterFees = netRevenue - cogs;
    const netAfterFees = grossAfterFees - opex;
    return { revenue, netRevenue, cogs, opex, net: netAfterFees, stripeFees: stripeFeesYtd };
  }, [state, currentYear, fromMonth, currentMonthIdx, stripeFeesYtd]);

  const cashCollectedYtd = useMemo(
    () => calculateCashCollected(payments, fromDate, toDate),
    [payments, fromDate, toDate]
  );

  // "New sales" agency-wide = sum of per-department new sales for filtered range.
  const newSalesYtd = useMemo(() => {
    let total = 0;
    for (let i = fromMonth - 1; i <= currentMonthIdx; i++) {
      const mKey = `${currentYear}-${String(i + 1).padStart(2, "0")}`;
      for (const dept of departments) {
        total += computeDepartmentNewSales(dept, state, mKey);
      }
      total += computeDepartmentNewSales(null, state, mKey);
    }
    return total;
  }, [departments, state, currentYear, fromMonth, currentMonthIdx]);

  const agencyCashInBank = useMemo(
    () => computeAgencyCashInBank(state, asOf),
    [state, asOf]
  );

  const arOutstanding = useMemo(() => calculateOutstandingAR(payments), [payments]);

  const dividendsYtd = useMemo(() => {
    return dividends
      .filter((d) => d.date && d.date >= fromDate && d.date <= toDate)
      .reduce((s, d) => s + (Number(d.amount) || 0), 0);
  }, [dividends, fromDate, toDate]);

  // Aggregate per-department budgets held (current month).
  const budgetsHeldTotal = useMemo(() => {
    let total = 0;
    for (const dept of departments) {
      total += computeDepartmentBudgetHeld(dept, state, currentMKey).held;
    }
    total += computeDepartmentBudgetHeld(null, state, currentMKey).held;
    return total;
  }, [departments, state, currentMKey]);

  // Per-department mini cards
  const deptCards = useMemo(() => {
    return departments.map((dept) => {
      const mrr = computeDepartmentMRR(dept, state, currentMKey);
      const cash = computeDepartmentCashCollected(dept, state, MONTH_START(currentYear, currentMonthIdx + 1), MONTH_END(currentYear, currentMonthIdx + 1));
      const bank = computeDepartmentCashInBank(dept, state, asOf);
      const next = computeNextInvoicesDue(dept, state, 1);
      return { dept, mrr, cash, bank, nextInvoice: next[0] || null };
    });
  }, [departments, state, currentMKey, currentYear, currentMonthIdx, asOf]);

  return (
    <div className="space-y-5">
      {/* Top — agency KPI strip */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-brand-slate mb-2">
          Agency · {currentYear} ·{" "}
          {fromMonth === 1 && toMonth === 12
            ? "Full year"
            : fromMonth === toMonth
            ? new Date(currentYear, fromMonth - 1).toLocaleString("en-US", { month: "short" })
            : `${new Date(currentYear, fromMonth - 1).toLocaleString("en-US", { month: "short" })}–${new Date(currentYear, toMonth - 1).toLocaleString("en-US", { month: "short" })}`}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard label="MRR" value={formatUSD(agencyMRR)} accent="blue" sublabel="active recurring" />
          <MetricCard label="Cash collected" value={formatUSD(cashCollectedYtd)} accent="green" sublabel="paid invoices YTD" />
          <MetricCard label="New sales" value={formatUSD(newSalesYtd)} accent="amber" sublabel="cash minus MRR" />
          <MetricCard label="Revenue (P&L)" value={formatUSD(ytdAgency.revenue)} accent="default" sublabel="recognized" />
          <MetricCard label="Net profit" value={formatUSD(ytdAgency.net)} accent={ytdAgency.net > 0 ? "green" : "red"} sublabel="agency-wide YTD" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricCard label="Stripe fees YTD" value={formatUSD(stripeFeesYtd)} accent={stripeFeesYtd > 0 ? "amber" : "default"} sublabel="deducted before bank" />
        <MetricCard label="Cash in Bank" value={formatUSD(agencyCashInBank)} accent={agencyCashInBank > 0 ? "green" : "red"} sublabel="cash − fees − expenses − dividends" />
        <MetricCard label="Client budgets held" value={formatUSD(budgetsHeldTotal)} accent={budgetsHeldTotal > 0 ? "amber" : "default"} sublabel="collected upfront · unspent" />
        <MetricCard label="Dividends YTD" value={formatUSD(dividendsYtd)} accent={dividendsYtd > 0 ? "amber" : "default"} sublabel="paid out to owner" />
        <MetricCard label="AR outstanding" value={formatUSD(arOutstanding)} accent={arOutstanding > 0 ? "amber" : "default"} sublabel="pending + scheduled invoices" />
      </div>

      {/* Per-department mini cards */}
      {deptCards.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-slate mb-2">
            Departments
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {deptCards.map((card) => (
              <button
                key={card.dept}
                onClick={() => onJumpToDept?.(card.dept)}
                className="text-left bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card p-4 hover:border-brand-blue hover:shadow-md transition"
                title={`Open ${card.dept} drill-down`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2 size={14} className="text-brand-blue" />
                  <div className="font-display font-semibold text-sm text-brand-ink dark:text-[var(--text-primary)]">
                    {card.dept}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-brand-slate uppercase tracking-wide text-[10px]">MRR</div>
                    <div className="font-semibold text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(card.mrr)}</div>
                  </div>
                  <div>
                    <div className="text-brand-slate uppercase tracking-wide text-[10px]">Cash {currentMKey.slice(5, 7) === String(currentMonthIdx + 1).padStart(2, "0") ? "(month)" : ""}</div>
                    <div className="font-semibold text-emerald-700 dark:text-emerald-300">{formatUSD(card.cash)}</div>
                  </div>
                  <div>
                    <div className="text-brand-slate uppercase tracking-wide text-[10px]">Cash in Bank</div>
                    <div className={`font-semibold ${card.bank > 0 ? "text-emerald-700 dark:text-emerald-300" : card.bank < 0 ? "text-red-600" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                      {formatUSD(card.bank)}
                    </div>
                  </div>
                  <div>
                    <div className="text-brand-slate uppercase tracking-wide text-[10px]">Next invoice</div>
                    {card.nextInvoice ? (
                      <div className="font-semibold text-brand-ink dark:text-[var(--text-primary)]">
                        {formatUSD(card.nextInvoice.amount)}
                        <span className="text-brand-slate font-normal ml-1">
                          {(() => {
                            const days = Math.max(0, Math.round((new Date(card.nextInvoice.dueDate) - t) / 86400000));
                            return `· ${days === 0 ? "today" : `${days}d`}`;
                          })()}
                        </span>
                      </div>
                    ) : (
                      <div className="text-brand-slate">—</div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
